import { useCallback, useMemo } from 'react';
import ProsemirrorEditor from 'hubspot-prosemirror/prosemirror/ProsemirrorEditor';
import { BLOCKQUOTE, BOLD, CLEAR_FORMATTING, FONT_STYLE, FONT_SIZE, INLINE_IMAGE, ITALIC, KNOWLEDGE_ARTICLE, LINK, LISTS, SNIPPETS, TEXT_ALIGN, TEXT_HIGHLIGHT, TEXT_COLOR, UNDERLINE } from 'hubspot-prosemirror/prosemirror/editorCapabilities';
import useHubSpotRTE from 'hubspot-prosemirror/prosemirror/useHubSpotRTE';
import { DrawerTypes } from 'file-manager-components/constants';
import identity from 'transmute/identity';
import { Map as ImmutableMap } from 'immutable';
import contentOutputs from 'hubspot-prosemirror/prosemirror/editorCapabilities/ContentOutputTypes';
// @ts-expect-error not typed
import SharedDragDropContext from 'customer-data-ui-utilities/dnd/SharedDragDropContext';
import { COMPANY, CONTACT, DEAL, TICKET } from 'customer-data-objects/constants/ObjectTypes';
import hubHttpClient from 'hub-http/clients/apiClient';
import createImageButton from 'hubspot-prosemirror/draft-transplant/components/createImageButton';
import SnippetsApi from '../../apis/SnippetsApi';
import { getPlaceholderPlugin } from 'hubspot-prosemirror/prosemirror/plugins/PlaceholderPlugin';
import { configureMentionsPlugin } from './plugins/configureMentionsPlugin';
import { configureSnippetsPlugin } from './plugins/configureSnippetsPlugin';
import { AsyncFileManager } from '../fileManager/AsyncFileManager';
import { styledWithTrellis } from '../../utils/trellis';
import { isStyleOnFill, useCommentsUseStyle } from '../../contexts/CommentsUseStyleContext';
import { jsx as _jsx } from "react/jsx-runtime";
const ProsemirrorStyledEditor = styledWithTrellis.div(({
  theme,
  isOnFill
}) => `
  .ProsemirrorEditor__wrapper {
    position: relative;
    border-radius: ${theme.borderRadius['200']};
    border: ${theme.borderWidth['100']} solid
      ${theme.color['border-core-default']};
    min-height: 150px;
     background: ${isOnFill ? theme.color['fill-field-default-alt'] : theme.color['fill-surface-raised']};
  }
  .ProseMirrorPlaceholder {
    position: absolute;
    pointer-events: none;
    color: ${theme.color['text-core-subtle']};
    line-height: 1.5;
  }
  .ProsemirrorEditor__body {
    min-height: 100px;
    display: flex;
    flex-direction: column;
  }
  .ProseMirror {
    flex-grow: 1;
    min-height: 96px;
    position: relative;
    overflow: auto;
    overflow-wrap: anywhere;
  }
`);
export function RichTextEditor({
  onChange,
  owners,
  mentionsPlugins,
  value = '',
  autoFocus = false,
  associations = ImmutableMap(),
  placeholder
}) {
  const [editorState, setEditorState] = useHubSpotRTE(value);
  const use = useCommentsUseStyle();
  const isOnFill = isStyleOnFill(use);
  const getSupplementalObjects = useCallback(() => {
    // Converts supported association IDs to a `supplementalObjects` object
    // passed to the backend for snippet rendering. New types (like custom
    // objects) will need to be added here to support tokens in snippets.
    const associationMapIdToObjectType = {
      contactIds: CONTACT,
      companyIds: COMPANY,
      dealIds: DEAL,
      ticketIds: TICKET
    };
    return associations.reduce((supplementalObjects, associationValues, associationKey) => {
      const associationValue = associationValues.first();
      // @ts-expect-error ts-migrate
      const key = associationMapIdToObjectType[associationKey];
      if (key && associationValue) {
        supplementalObjects[key] = associationValue;
      }
      return supplementalObjects;
    }, {});
  }, [associations]);
  const snippetProps = useMemo(() => ({
    snippetFunctions: SnippetsApi(hubHttpClient),
    contactEmail: '',
    contactVid: undefined,
    supplementalObjects: getSupplementalObjects()
  }), [getSupplementalObjects]);

  // Crucial that editor config is not recreated on every render cycle
  const editorConfig = useMemo(() => {
    return {
      [BOLD]: {},
      [ITALIC]: {},
      [UNDERLINE]: {},
      [CLEAR_FORMATTING]: {},
      [TEXT_ALIGN]: {},
      [LISTS]: {},
      [LINK]: {},
      [BLOCKQUOTE]: {},
      [TEXT_HIGHLIGHT]: {},
      [TEXT_COLOR]: {},
      [FONT_STYLE]: {},
      [FONT_SIZE]: {},
      [INLINE_IMAGE]: {
        ImageButton: createImageButton({
          useOptionsPopover: true,
          shouldScaleInserts: true,
          httpClient: hubHttpClient,
          FileManager: AsyncFileManager,
          SharedDragDropContext,
          drawerType: DrawerTypes.IMAGE
        })
      },
      [KNOWLEDGE_ARTICLE]: {
        onInsertContent: identity,
        onOpenPopover: identity
      },
      [SNIPPETS]: {
        onInsertContent: identity,
        onOpenPopover: identity,
        outputType: contentOutputs.RENDERED,
        apiWrapper: SnippetsApi
      }
    };
  }, []);
  const customPlugins = useMemo(() => {
    return [...(placeholder ? [getPlaceholderPlugin(placeholder)] : []), ...(mentionsPlugins !== null && mentionsPlugins !== void 0 ? mentionsPlugins : [configureMentionsPlugin({
      owners
    })]), configureSnippetsPlugin(Object.assign({}, snippetProps))];
  }, [owners, mentionsPlugins, snippetProps, placeholder]);
  const handleEditorStateChange = useCallback(newEditorState => {
    setEditorState(newEditorState);
    onChange(newEditorState);
  }, [onChange, setEditorState]);
  return (
    /*#__PURE__*/
    /* @bend-expect trellis-tokenized */
    _jsx(ProsemirrorStyledEditor, {
      "data-test-id": "rte-container",
      isOnFill: isOnFill,
      children: /*#__PURE__*/_jsx(ProsemirrorEditor, {
        editorState: editorState,
        setEditorState: handleEditorStateChange,
        editorConfig: editorConfig,
        customPlugins: customPlugins,
        autoFocus: autoFocus
      })
    })
  );
}